/**
 * Management console i18n — FR default + AR-RTL. Registered once in the module's onRegister(). Keys are
 * namespaced under `mgmt.*` and `nav.mgmt.*` / `nav.section.platform`. Layout mirrors automatically under
 * dir="rtl" via CSS logical properties; codes / amounts / dates stay LTR with .nv-ltr-island. Missing AR
 * key → the FR value → the raw key (never a crash, never a blank).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── nav ──────────────────────────────────────────────────────────────────────
  "nav.section.platform": "Plateforme",
  "nav.mgmt.overview": "Tableau de bord",
  "nav.mgmt.requests": "Demandes d'accès",
  "nav.mgmt.onboarding": "Laboratoires",
  "nav.mgmt.paramtypes": "Types de paramètres",
  "nav.mgmt.subscriptions": "Abonnements",
  "nav.mgmt.pricing": "Tarification",
  "nav.mgmt.support": "Support",
  "nav.mgmt.analytics": "Analytique",
  "nav.mgmt.analyzers": "Parc d'automates",

  // ── shared ─────────────────────────────────────────────────────────────────────
  "mgmt.action.refresh": "Actualiser",
  "mgmt.empty.none": "Aucune donnée.",
  "mgmt.console.title": "Console plateforme",
  "mgmt.unavailable.title": "Console indisponible sur cette installation",
  "mgmt.unavailable.desc":
    "La console plateforme est hébergée au central Pharmakon et n'est pas disponible sur une installation locale.",

  // ── Overview (Tableau de bord) ──────────────────────────────────────────────────
  "mgmt.overview.title": "Console plateforme",
  "mgmt.overview.subtitle": "Vue d'ensemble de l'écosystème : laboratoires, abonnements et support.",
  "mgmt.overview.empty.title": "Aucune donnée plateforme",
  "mgmt.overview.empty.desc": "Aucune métrique n'est encore disponible pour cette plateforme.",
  "mgmt.overview.limits.note":
    "Les compteurs sont instantanés ; les écarts période-sur-période s'appuient sur les dates de création/mise à jour, non sur un historique de snapshots.",

  // KPI grid
  "mgmt.overview.kpi.labs": "Laboratoires",
  "mgmt.overview.kpi.labs.hint": "Laboratoires actifs sur la plateforme",
  "mgmt.overview.kpi.subscriptions": "Abonnements actifs",
  "mgmt.overview.kpi.subscriptions.hint": "Plans en essai, actifs ou en grâce",
  "mgmt.overview.kpi.churn": "Résiliations (30 j)",
  "mgmt.overview.kpi.churn.hint": "Abonnements annulés sur les 30 derniers jours",
  "mgmt.overview.kpi.tickets": "Tickets ouverts",
  "mgmt.overview.kpi.tickets.hint": "Demandes de support en attente de traitement",
  "mgmt.overview.kpi.sla": "Respect SLA",
  "mgmt.overview.kpi.sla.hint": "Tickets résolus sous {h} h (90 derniers jours)",
  "mgmt.overview.kpi.aged": "Tickets en retard",
  "mgmt.overview.kpi.aged.hint": "Tickets ouverts depuis plus de {d} jours",
  "mgmt.overview.kpi.suspended": "Laboratoires suspendus",
  "mgmt.overview.kpi.suspended.hint": "Comptes désactivés (cycle de vie)",
  "mgmt.overview.kpi.unread": "Messages non lus",
  "mgmt.overview.kpi.unread.hint": "Réponses de laboratoires à lire",
  "mgmt.overview.delta.vsPrevMonth": "vs mois dernier",
  "mgmt.overview.delta.vsPrev30d": "vs 30 j préc.",

  // Period picker
  "mgmt.overview.period.label": "Période des tendances",
  "mgmt.overview.period.30d": "30 j",
  "mgmt.overview.period.90d": "90 j",
  "mgmt.overview.period.12mo": "12 mois",

  // Alerts strip
  "mgmt.overview.alerts.title": "Alertes plateforme",
  "mgmt.overview.alert.sla": "Respect SLA à {pct} % — sous l'objectif de {target} h",
  "mgmt.overview.alert.sla.cta": "Voir les tickets",
  "mgmt.overview.alert.aged": "{count} ticket(s) ouvert(s) depuis trop longtemps",
  "mgmt.overview.alert.aged.cta": "Traiter",
  "mgmt.overview.alert.churn": "{count} résiliation(s) sur 30 j (préc. {prev})",
  "mgmt.overview.alert.churn.cta": "Voir les annulations",
  "mgmt.overview.alert.stall": "{count} laboratoire(s) provisionné(s) sans abonnement synchronisé",
  "mgmt.overview.alert.stall.cta": "Voir les abonnements",
  "mgmt.overview.alert.unread": "{count} message(s) de laboratoires non lu(s)",
  "mgmt.overview.alert.unread.cta": "Ouvrir le support",

  // Chart region
  "mgmt.overview.chart.empty.title": "Pas encore de données",
  "mgmt.overview.chart.empty.desc": "Les données s'afficheront dès que l'activité de la plateforme aura commencé.",
  "mgmt.overview.chart.labGrowth.title": "Croissance des laboratoires",
  "mgmt.overview.chart.labGrowth.tip": "Total laboratoires",
  "mgmt.overview.chart.labGrowth.aria": "Courbe cumulée des laboratoires, total final {n}",
  "mgmt.overview.chart.subscriptions.title": "Abonnements par statut",
  "mgmt.overview.chart.subscriptions.aria": "Répartition des abonnements par statut",
  "mgmt.overview.chart.byCategory.title": "Tickets par catégorie",
  "mgmt.overview.chart.byCategory.tip": "Tickets",
  "mgmt.overview.chart.byCategory.aria": "Tickets de support répartis par catégorie",
  "mgmt.overview.chart.ticketStatus.title": "Tickets par statut",
  "mgmt.overview.chart.ticketStatus.aria": "Répartition des tickets par statut",
  "mgmt.overview.chart.funnel.title": "Entonnoir d'intégration",
  "mgmt.overview.chart.donut.sr": "{n} segments",

  // Funnel stages
  "mgmt.overview.funnel.provisioned": "Provisionnés",
  "mgmt.overview.funnel.synced": "Abonnement synchronisé",
  "mgmt.overview.funnel.activeSub": "Abonnement actif",
  "mgmt.overview.funnel.everLoggedIn": "Connectés au moins une fois",

  // Distribution labels (owned by the overview so they resolve without the section packs)
  "mgmt.overview.subStatut.ESSAI": "Essai",
  "mgmt.overview.subStatut.ACTIVE": "Actif",
  "mgmt.overview.subStatut.PAST_DUE": "Impayé (grâce)",
  "mgmt.overview.subStatut.SUSPENDUE": "Suspendu",
  "mgmt.overview.subStatut.EXPIREE": "Expiré",
  "mgmt.overview.subStatut.ANNULEE": "Annulé",
  "mgmt.overview.ticketStatut.ouvert": "Ouvert",
  "mgmt.overview.ticketStatut.en_cours": "En cours",
  "mgmt.overview.ticketStatut.resolu": "Résolu",
  "mgmt.overview.ticketStatut.ferme": "Clôturé",
  "mgmt.overview.cat.facturation": "Facturation",
  "mgmt.overview.cat.technique": "Technique",
  "mgmt.overview.cat.donnees": "Données",
  "mgmt.overview.cat.compte": "Compte",
  "mgmt.overview.cat.autre": "Autre",

  "mgmt.overview.quick.title": "Accès rapides",
  "mgmt.overview.quick.onboarding": "Gérer les laboratoires",
  "mgmt.overview.quick.paramtypes": "Catalogue des paramètres",
  "mgmt.overview.quick.subscriptions": "Gérer les abonnements",
  "mgmt.overview.quick.support": "Ouvrir le support",

  // ── Section placeholders (the screen agents replace these bodies) ───────────────
  "mgmt.onboarding.title": "Laboratoires",
  "mgmt.onboarding.subtitle": "Provisionnement et cycle de vie des laboratoires (cross-lab).",
  "mgmt.paramtypes.title": "Types de paramètres",
  "mgmt.paramtypes.subtitle": "Catalogue global des types d'analyses (publié vers tous les laboratoires).",
  "mgmt.subscriptions.title": "Abonnements",
  "mgmt.subscriptions.subtitle": "Plans et droits par laboratoire (projection centrale autoritaire).",
  "mgmt.support.title": "Support",
  "mgmt.support.subtitle": "Triage des tickets et fil de discussion cross-lab.",
  "mgmt.section.placeholder": "Cet écran est en cours de construction.",
};

const AR: Record<string, string> = {
  // ── nav ──────────────────────────────────────────────────────────────────────
  "nav.section.platform": "المنصة",
  "nav.mgmt.overview": "لوحة القيادة",
  "nav.mgmt.requests": "طلبات الوصول",
  "nav.mgmt.onboarding": "المختبرات",
  "nav.mgmt.paramtypes": "أنواع المعايير",
  "nav.mgmt.subscriptions": "الاشتراكات",
  "nav.mgmt.pricing": "التسعير",
  "nav.mgmt.support": "الدعم",
  "nav.mgmt.analytics": "التحليلات",
  "nav.mgmt.analyzers": "أسطول الأجهزة",

  // ── shared ─────────────────────────────────────────────────────────────────────
  "mgmt.action.refresh": "تحديث",
  "mgmt.empty.none": "لا توجد بيانات.",
  "mgmt.console.title": "وحدة تحكم المنصة",
  "mgmt.unavailable.title": "وحدة التحكم غير متاحة على هذا التثبيت",
  "mgmt.unavailable.desc":
    "وحدة تحكم المنصة مُستضافة في مركز Pharmakon وغير متاحة على التثبيت المحلي.",

  // ── Overview ─────────────────────────────────────────────────────────────────
  "mgmt.overview.title": "وحدة تحكم المنصة",
  "mgmt.overview.subtitle": "نظرة عامة على المنظومة: المختبرات والاشتراكات والدعم.",
  "mgmt.overview.empty.title": "لا توجد بيانات للمنصة",
  "mgmt.overview.empty.desc": "لا تتوفر أي مؤشرات لهذه المنصة بعد.",
  "mgmt.overview.limits.note":
    "العدادات لحظية؛ تعتمد فروقات الفترة على تواريخ الإنشاء/التحديث وليس على سجل لقطات.",

  "mgmt.overview.kpi.labs": "المختبرات",
  "mgmt.overview.kpi.labs.hint": "المختبرات النشطة على المنصة",
  "mgmt.overview.kpi.subscriptions": "الاشتراكات النشطة",
  "mgmt.overview.kpi.subscriptions.hint": "الخطط التجريبية أو النشطة أو في فترة السماح",
  "mgmt.overview.kpi.churn": "إلغاءات (30 يوم)",
  "mgmt.overview.kpi.churn.hint": "الاشتراكات الملغاة خلال آخر 30 يومًا",
  "mgmt.overview.kpi.tickets": "التذاكر المفتوحة",
  "mgmt.overview.kpi.tickets.hint": "طلبات الدعم بانتظار المعالجة",
  "mgmt.overview.kpi.sla": "الالتزام بـ SLA",
  "mgmt.overview.kpi.sla.hint": "التذاكر المحلولة خلال {h} ساعة (آخر 90 يومًا)",
  "mgmt.overview.kpi.aged": "تذاكر متأخرة",
  "mgmt.overview.kpi.aged.hint": "تذاكر مفتوحة منذ أكثر من {d} أيام",
  "mgmt.overview.kpi.suspended": "مختبرات موقوفة",
  "mgmt.overview.kpi.suspended.hint": "حسابات معطّلة (دورة الحياة)",
  "mgmt.overview.kpi.unread": "الرسائل غير المقروءة",
  "mgmt.overview.kpi.unread.hint": "ردود المختبرات بانتظار القراءة",
  "mgmt.overview.delta.vsPrevMonth": "مقابل الشهر الماضي",
  "mgmt.overview.delta.vsPrev30d": "مقابل 30 يومًا سابقة",

  "mgmt.overview.period.label": "فترة الاتجاهات",
  "mgmt.overview.period.30d": "30 يوم",
  "mgmt.overview.period.90d": "90 يوم",
  "mgmt.overview.period.12mo": "12 شهرًا",

  "mgmt.overview.alerts.title": "تنبيهات المنصة",
  "mgmt.overview.alert.sla": "الالتزام بـ SLA عند {pct}٪ — دون هدف {target} ساعة",
  "mgmt.overview.alert.sla.cta": "عرض التذاكر",
  "mgmt.overview.alert.aged": "{count} تذكرة مفتوحة منذ مدة طويلة",
  "mgmt.overview.alert.aged.cta": "المعالجة",
  "mgmt.overview.alert.churn": "{count} إلغاء خلال 30 يومًا (السابق {prev})",
  "mgmt.overview.alert.churn.cta": "عرض الإلغاءات",
  "mgmt.overview.alert.stall": "{count} مختبر مُجهّز دون اشتراك متزامن",
  "mgmt.overview.alert.stall.cta": "عرض الاشتراكات",
  "mgmt.overview.alert.unread": "{count} رسالة غير مقروءة من المختبرات",
  "mgmt.overview.alert.unread.cta": "فتح الدعم",

  "mgmt.overview.chart.empty.title": "لا توجد بيانات بعد",
  "mgmt.overview.chart.empty.desc": "ستظهر البيانات بمجرد بدء نشاط المنصة.",
  "mgmt.overview.chart.labGrowth.title": "نمو المختبرات",
  "mgmt.overview.chart.labGrowth.tip": "إجمالي المختبرات",
  "mgmt.overview.chart.labGrowth.aria": "منحنى تراكمي للمختبرات، الإجمالي النهائي {n}",
  "mgmt.overview.chart.subscriptions.title": "الاشتراكات حسب الحالة",
  "mgmt.overview.chart.subscriptions.aria": "توزيع الاشتراكات حسب الحالة",
  "mgmt.overview.chart.byCategory.title": "التذاكر حسب الفئة",
  "mgmt.overview.chart.byCategory.tip": "تذاكر",
  "mgmt.overview.chart.byCategory.aria": "تذاكر الدعم موزعة حسب الفئة",
  "mgmt.overview.chart.ticketStatus.title": "التذاكر حسب الحالة",
  "mgmt.overview.chart.ticketStatus.aria": "توزيع التذاكر حسب الحالة",
  "mgmt.overview.chart.funnel.title": "مسار الإعداد",
  "mgmt.overview.chart.donut.sr": "{n} أجزاء",

  "mgmt.overview.funnel.provisioned": "مُجهّزة",
  "mgmt.overview.funnel.synced": "اشتراك متزامن",
  "mgmt.overview.funnel.activeSub": "اشتراك نشط",
  "mgmt.overview.funnel.everLoggedIn": "سجّلوا الدخول مرة واحدة على الأقل",

  "mgmt.overview.subStatut.ESSAI": "تجريبي",
  "mgmt.overview.subStatut.ACTIVE": "نشط",
  "mgmt.overview.subStatut.PAST_DUE": "متأخر الدفع (سماح)",
  "mgmt.overview.subStatut.SUSPENDUE": "موقوف",
  "mgmt.overview.subStatut.EXPIREE": "منتهٍ",
  "mgmt.overview.subStatut.ANNULEE": "ملغى",
  "mgmt.overview.ticketStatut.ouvert": "مفتوح",
  "mgmt.overview.ticketStatut.en_cours": "قيد المعالجة",
  "mgmt.overview.ticketStatut.resolu": "تم الحل",
  "mgmt.overview.ticketStatut.ferme": "مغلق",
  "mgmt.overview.cat.facturation": "الفوترة",
  "mgmt.overview.cat.technique": "تقني",
  "mgmt.overview.cat.donnees": "البيانات",
  "mgmt.overview.cat.compte": "الحساب",
  "mgmt.overview.cat.autre": "أخرى",

  "mgmt.overview.quick.title": "وصول سريع",
  "mgmt.overview.quick.onboarding": "إدارة المختبرات",
  "mgmt.overview.quick.paramtypes": "كتالوج المعايير",
  "mgmt.overview.quick.subscriptions": "إدارة الاشتراكات",
  "mgmt.overview.quick.support": "فتح الدعم",

  // ── Section placeholders ─────────────────────────────────────────────────────
  "mgmt.onboarding.title": "المختبرات",
  "mgmt.onboarding.subtitle": "تجهيز المختبرات ودورة حياتها (عبر المختبرات).",
  "mgmt.paramtypes.title": "أنواع المعايير",
  "mgmt.paramtypes.subtitle": "الكتالوج العالمي لأنواع التحاليل (يُنشر لجميع المختبرات).",
  "mgmt.subscriptions.title": "الاشتراكات",
  "mgmt.subscriptions.subtitle": "الخطط والحقوق لكل مختبر (الإسقاط المركزي المرجعي).",
  "mgmt.support.title": "الدعم",
  "mgmt.support.subtitle": "فرز التذاكر وسلسلة المحادثة عبر المختبرات.",
  "mgmt.section.placeholder": "هذه الشاشة قيد الإنشاء.",
};

let registered = false;
export function registerManagementMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
